import React, { useEffect, useState } from "react";
import { Container, Row, Col, Accordion, Card } from "react-bootstrap";
import Rellax from "rellax";
import { AiOutlineDownload } from "react-icons/ai";
import { BsCheck2Circle } from "react-icons/bs";
import profile from "../../Assets/klean-leaf-profile.pdf";
import catalog from "../../Assets/klean_leaf_catalog.pdf";
import Techstack from "./Techstack";
import ContactModal from "../ContactModal/ContactModal";
import ohio from "../../Assets/blogs/klean-leaf-columbus-ohio-location-map.webp";
import factoryLineImage from "../../Assets/blogs/blog-hero.webp";
import manufactureImage from "../../Assets/blogs/manufacture.webp";
import workflowImage from "../../Assets/blogs/private-label-laundry-detergent-sheets-launch-workflow.webp";
import formulaTestingImage from "../../Assets/blogs/laundry-detergent-sheet-formula-testing.webp";
import privateLabelBoxImage from "../../Assets/blogs/your-brand-laundry-detergent-sheets-box.webp";
import truckImage from "../../Assets/shipping-truck.webp";
import bubbles from "../../Assets/bubbles.png";
import basket from "../../Assets/basket-bg.png";
import hanger from "../../Assets/clothes-hanger.png";
import kleanleaf from "../../Assets/kleanleaf.png";
import Seo, { breadcrumbSchema, faqSchema, organizationSchema, webPageSchema } from "../Seo/Seo";

const aboutFaqs = [
  {
    question: "What is Klean Leaf?",
    answer: "Klean Leaf is a Columbus, Ohio based private label support team for laundry detergent sheet buyers. We help brands and business buyers develop, package, and source laundry detergent sheets through our manufacturing network.",
  },
  {
    question: "Who does Klean Leaf help?",
    answer: "We work with brands, wholesalers, distributors, refill shops, retailers, e-commerce sellers, and laundry care companies that want to launch or expand a laundry detergent sheet product line.",
  },
  {
    question: "What is the minimum order quantity?",
    answer: "MOQ starts at 1,000 retail package units per SKU.",
  },
  {
    question: "What packaging options are available?",
    answer: "Common packaging options include paper envelope packaging and perforated paper boxes. After the client confirms the package type, we provide the dieline for artwork preparation.",
  },
  {
    question: "Do you offer sample packs?",
    answer: "Yes. We can ship sample sheets for formula, fragrance, and product review before the client confirms packaging and production.",
  },
  {
    question: "Can you customize fragrance?",
    answer: "Yes. We can support fragrance-free sheets and multiple fragrance options. Clients can review available fragrance samples before confirming their final direction.",
  },
  {
    question: "Where are Klean Leaf products manufactured?",
    answer: "Klean Leaf's client support team is based in Columbus, Ohio. Production is handled through our manufacturing network in China, where the sheets are formulated, produced, packed, and prepared for shipment.",
  },
  {
    question: "What support do you offer for private label clients?",
    answer: "We support product discussion, sample shipment, fragrance selection, packaging option review, dieline sharing, artwork coordination, production follow-up, inspection support, and logistics coordination.",
  },
];

const storyStats = [
  "MOQ starts at 1,000 retail package units per SKU",
  "45–60 days for first private label orders",
  "Multiple fragrance and packaging options",
  "Columbus, Ohio based client support",
  "Private label and wholesale support",
];

const coreValues = [
  {
    title: "Sheet-Based Cleaning for Modern Brands",
    image: workflowImage,
    alt: "Laundry detergent sheet in a washing machine drum",
    copy: "Laundry detergent sheets offer a compact and lightweight format for brands looking to move beyond traditional liquid detergent bottles. They are easy to store, easy to ship, and simple for customers to use. Klean Leaf focuses on helping brands develop laundry sheet products that balance convenience, performance, and retail-ready presentation.",
  },
  {
    title: "R&D and Formula Improvement",
    image: formulaTestingImage,
    alt: "Laundry detergent sheet formula testing and R&D review",
    copy: "Laundry sheet performance depends on formula balance, sheet texture, dissolving speed, fragrance, and cleaning experience. Klean Leaf works with manufacturing and product teams to review sample feedback and improve product details before production. If a buyer needs fragrance-free sheets, fresh linen sheets, sensitive-use positioning, or customized fragrance options, we help guide the sample and confirmation process.",
  },
  {
    title: "Packaging Built for Retail",
    image: privateLabelBoxImage,
    alt: "Private label laundry detergent sheets box for custom brand packaging",
    copy: "A good laundry sheet product needs packaging that protects the sheets, communicates the brand clearly, and works for the sales channel. Klean Leaf supports paper envelope packaging, perforated box packaging, and other private label options depending on the project. We provide dielines after packaging selection so the buyer's designer can prepare print-ready artwork.",
  },
  {
    title: "Reliable Production and Logistics Support",
    image: truckImage,
    alt: "Klean Leaf cartons being loaded into a shipping container for logistics coordination",
    copy: "From sample shipment to first order production, Klean Leaf helps buyers understand lead time, packaging requirements, order quantities, and shipping details. Our Columbus, Ohio team communicates with clients in the U.S. time zone, while our manufacturing network handles production, packing, and shipment coordination.",
  },
];

const differenceCards = [
  {
    title: "Production Capability",
    copy: "Klean Leaf supports private label laundry sheet projects through experienced manufacturing resources for sheet-form cleaning products. Our production support covers laundry detergent sheets, packaging preparation, sample confirmation, and retail order fulfillment.",
  },
  {
    title: "R&D Support",
    copy: "We help buyers compare sheet texture, dissolving speed, fragrance strength, and cleaning performance during the sampling stage. Feedback from sample review can be used to adjust the product direction before production.",
  },
  {
    title: "Private Label Packaging",
    copy: "Klean Leaf supports custom packaging for private label brands, including envelope-style packaging and perforated boxes. Once the package type is confirmed, we provide the dieline so your design team can prepare artwork.",
  },
  {
    title: "U.S.-Based Communication",
    copy: "Our Columbus, Ohio office provides easier communication for North American buyers. Clients can discuss product options, sample shipments, artwork review, production schedules, and logistics in the Eastern Time Zone.",
  },
];

function About() {
  const [activeKey, setActiveKey] = useState("0");
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const handleToggle = (key) => {
    setActiveKey(activeKey === key ? null : key);
  };

  const showContactModal = () => setIsContactModalOpen(true);
  const handleContactCancel = () => setIsContactModalOpen(false);

  // Initialize Rellax (parallax effect)
  useEffect(() => {
    // Check if elements with the class 'rellax' exist before initializing Rellax
    const elements = document.querySelectorAll('.rellax');
    if (elements.length > 0) {
      const rellax = new Rellax('.rellax');
  
      // Cleanup function to destroy the instance if it exists
      return () => {
        if (rellax && typeof rellax.destroy === 'function') {
          rellax.destroy();
        }
      };
    }
  }, []);

  return (
    <Container fluid className="about-section page-top-settings" id="about">
      <Seo
        title="About Klean Leaf | Private Label Laundry Detergent Sheet Manufacturer"
        description="Meet Klean Leaf, a Columbus Ohio private label support team for laundry detergent sheets, custom laundry sheets, eco-friendly laundry packaging, wholesale programs, and detergent sheet production."
        path="/about"
        schema={[
          organizationSchema,
          webPageSchema({
            name: "About Klean Leaf | Private Label Laundry Detergent Sheet Manufacturer",
            description: "Meet Klean Leaf, a Columbus Ohio private label support team for laundry detergent sheets, custom laundry sheets, eco-friendly laundry packaging, wholesale programs, and detergent sheet production.",
            path: "/about",
            primaryImage: ohio,
          }),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "About", path: "/about" },
          ]),
          faqSchema(aboutFaqs),
        ]}
      />

      <div className="background-wrapper">
        <img src={bubbles} alt="" aria-hidden="true" className="rellax background1" data-rellax-speed="7" />
        <img src={hanger} alt="" aria-hidden="true" className="rellax background2" data-rellax-speed="-4" />
        <img src={basket} alt="" aria-hidden="true" className="rellax background4" data-rellax-speed="6" />
        <img src={kleanleaf} alt="" aria-hidden="true" className="rellax background5" data-rellax-speed="8" />
      </div>

      <section className="about-hero">
        <Container>
          <Row className="align-items-center">
            <Col lg={7} className="about-hero-copy">
              <p className="section-kicker">Columbus, Ohio private label support</p>
              <h1>Private Label Laundry Detergent Sheet Manufacturer</h1>
              <p className="about-hero-subtitle">
                Columbus, Ohio support for private label laundry sheet buyers
              </p>
              <p>
                Klean Leaf helps brands, retailers, wholesalers, refill shops, distributors, and e-commerce teams launch laundry detergent sheets under their own brand. From product development and fragrance selection to packaging, sampling, production coordination, quality control, and logistics, we support buyers through every step of building a retail-ready laundry sheet program.
              </p>
              <div className="about-actions">
                <a className="secondary-btn" href={profile} target="_blank" rel="noopener noreferrer">
                  <AiOutlineDownload />
                  <span>Klean Leaf Profile</span>
                </a>
                <a className="secondary-btn" href={catalog} target="_blank" rel="noopener noreferrer">
                  <AiOutlineDownload />
                  <span>Catalog</span>
                </a>
                <button className="primary-btn about-contact-button" type="button" onClick={showContactModal}>
                  Request Samples or Quote
                </button>
              </div>
            </Col>
            <Col lg={5} className="about-hero-media">
              <img src={factoryLineImage} alt="Laundry detergent sheet manufacturing line" loading="lazy" decoding="async" />
            </Col>
          </Row>
        </Container>
      </section>

      <section className="about-light-section about-welcome">
        <Container>
          <Row className="justify-content-center">
            <Col lg={9} className="about-section-intro">
              <p className="section-kicker">About Klean Leaf</p>
              <h2>Welcome to Klean Leaf</h2>
              <p>
                Klean Leaf provides end-to-end support for private label laundry detergent sheet programs. We work with business buyers who want a lighter, cleaner, and more modern alternative to traditional bottled laundry detergent.
              </p>
              <p>
                Our service covers product planning, formula discussion, fragrance options, sample review, retail packaging, production coordination, inspection, and shipping support. Whether you are launching a new laundry care brand, adding laundry sheets to an existing product line, or sourcing a refill-format product for wholesale and retail channels, Klean Leaf helps simplify the process from concept to delivery.
              </p>
              <p>
                Our office and client support team are based in Columbus, Ohio, while our manufacturing network supports formulation, production, and packaging for laundry detergent sheets and related cleaning sheet products.
              </p>
              <div className="about-inline-actions">
                <button className="primary-btn" type="button" onClick={showContactModal}>
                  Contact Us About Your Project
                </button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="about-light-section about-vision">
        <Container>
          <Row className="align-items-center">
            <Col lg={5} className="about-image-panel">
              <img src={privateLabelBoxImage} alt="Retail-ready private label laundry detergent sheet packaging" loading="lazy" decoding="async" />
            </Col>
            <Col lg={7} className="about-copy-panel">
              <p className="section-kicker">Our vision</p>
              <h2>Our Vision: A Cleaner Everyday</h2>
              <p>
                At Klean Leaf, we believe laundry care should be simple, efficient, and easier to ship, store, and use. Laundry detergent sheets are designed to reduce bulky packaging, simplify daily laundry routines, and give brands a compact product format that works well for modern retail, e-commerce, refill, and subscription channels.
              </p>
              <p>
                Our goal is to help more businesses bring practical, well-designed laundry sheet products to market with dependable production support, clear communication, and flexible private label options.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="about-light-section about-story">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="about-story-media">
              <img src={factoryLineImage} alt="Laundry detergent sheet production line and manufacturing resources" loading="lazy" decoding="async" />
            </Col>
            <Col lg={6} className="about-story-copy">
              <p className="section-kicker">Who we are</p>
              <h2>Our Story</h2>
              <p>
                Klean Leaf was built to support brands that want to enter or expand in the laundry sheet market without having to manage every detail of overseas production on their own. Our team connects U.S.-based communication and project management with experienced manufacturing resources, helping buyers move from sample review to finished retail packaging more smoothly.
              </p>
              <p>
                We understand that private label buyers care about more than just price. Formula performance, sheet feel, fragrance, packaging quality, lead time, documentation, and delivery all matter. That is why we support each project with a clear workflow, from initial product discussion to sample shipment, packaging dielines, artwork review, production, and logistics coordination.
              </p>
              <div className="about-stat-list">
                {storyStats.map((stat) => (
                  <div className="about-stat-item" key={stat}>
                    <BsCheck2Circle />
                    <span>{stat}</span>
                  </div>
                ))}
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="about-values-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={9} className="about-section-intro">
              <p className="section-kicker">Core values</p>
              <h2>Built for Modern Laundry Care Programs</h2>
              <p>
                Klean Leaf combines practical product guidance, detergent sheet production support, eco-friendly laundry packaging options, and logistics coordination for custom laundry sheets and laundry sheets wholesale programs.
              </p>
            </Col>
          </Row>

          {coreValues.map((card, index) => (
            <Row className={`value-row ${index % 2 === 1 ? "value-row-reverse" : ""}`} key={card.title}>
              <Col lg={6} className="value-image">
                <img src={card.image} alt={card.alt} loading="lazy" decoding="async" />
              </Col>
              <Col lg={6} className="value-copy">
                <h3>{card.title}</h3>
                <p>{card.copy}</p>
              </Col>
            </Row>
          ))}
        </Container>
      </section>

      <section className="about-difference-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={9} className="about-section-intro">
              <p className="section-kicker">Private label support</p>
              <h2>What Makes Klean Leaf Different?</h2>
              <p>
                Business buyers get a clearer path from idea to retail-ready product, with Columbus Ohio private label support and manufacturing resources for detergent sheet production.
              </p>
            </Col>
          </Row>
          <Row className="difference-grid">
            {differenceCards.map((card) => (
              <Col md={6} lg={3} key={card.title}>
                <div className="difference-card">
                  <h3>{card.title}</h3>
                  <p>{card.copy}</p>
                </div>
              </Col>
            ))}
          </Row>
          <div className="about-section-cta">
            <button className="primary-btn" type="button" onClick={showContactModal}>
              Talk to Our Team
            </button>
          </div>
        </Container>
      </section>

      <section className="about-program-section">
        <Container>
          <Row className="about-program-layout align-items-center">
            <Col lg={7} className="about-program-content">
              <div className="about-section-intro">
              <p className="section-kicker">Program builder</p>
              <h2>Build Your Laundry Sheet Program with Klean Leaf</h2>
              </div>
              <Techstack />
            </Col>
            <Col lg={5} className="about-program-media">
              <img
                src={manufactureImage}
                alt="Klean Leaf manufacturing support facility"
                loading="lazy"
                decoding="async"
              />
            </Col>
          </Row>
          <div className="about-section-cta about-section-cta-light">
            <button className="secondary-btn" type="button" onClick={showContactModal}>
              Ask About Private Label Support
            </button>
          </div>
        </Container>
      </section>

      <section className="about-faq-section">
        <Container>
          <Row className="features-section">
            <Col md={12} className="features-title">
              <p className="section-kicker">FAQ</p>
              <h2>Frequently Asked Questions</h2>
            </Col>
            <Col lg={5} className="features-image">
              <img className="feature-team" src={ohio} alt="Private label laundry detergent sheet manufacturing facility" loading="lazy" decoding="async" />
            </Col>
            <Col lg={7}>
              <Accordion activeKey={activeKey}>
                {aboutFaqs.map((faq, index) => {
                  const eventKey = String(index);

                  return (
                    <Card key={faq.question}>
                      <h2 className="accordion-header">
                        <button
                          className={`accordion-button${activeKey === eventKey ? "" : " collapsed"}`}
                          type="button"
                          onClick={() => handleToggle(eventKey)}
                          aria-expanded={activeKey === eventKey}
                          aria-controls={`about-faq-${eventKey}`}
                        >
                          <span>{faq.question}</span>
                        </button>
                      </h2>
                      <Accordion.Collapse eventKey={eventKey} id={`about-faq-${eventKey}`}>
                        <Card.Body>
                          <p>{faq.answer}</p>
                        </Card.Body>
                      </Accordion.Collapse>
                    </Card>
                  );
                })}
              </Accordion>
            </Col>
          </Row>
          <div className="about-faq-cta">
            <button className="primary-btn" type="button" onClick={showContactModal}>
              Contact Klean Leaf
            </button>
          </div>
        </Container>
      </section>

      <ContactModal
        isModalOpen={isContactModalOpen}
        handleCancel={handleContactCancel}
        idPrefix="about-page-contact"
      />
    </Container>
  );
}

export default About;
